import { useState } from "react"
import { CaretDown, CaretUp } from "@phosphor-icons/react"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { CalendarFooter } from "@/components/ui/calendar"
import { cn } from "@/lib/utils"

const DISPLAY_YEAR_COUNT = 12

interface YearPickerProps extends Omit<React.ComponentPropsWithoutRef<"div">, "onSelect"> {
  defaultSelected?: number
  fromYear?: number
  onSelect?: (year: number) => void
  onReset?(): void
  onNextClick?: () => void
  onPrevClick?: () => void
}

export const YearPicker = ({
  className,
  defaultSelected = new Date().getFullYear(),
  onSelect,
  onReset,
  fromYear = new Date().getFullYear() - DISPLAY_YEAR_COUNT,
  children,
  ...props
}: YearPickerProps) => {
  const today = new Date()
  const currentYear = today.getFullYear()

  const [startYear, setStartYear] = useState<number>(fromYear!)
  const [selected, setSelected] = useState<number | null>(defaultSelected!)

  const yearsList: number[] = []
  const endYear = startYear + DISPLAY_YEAR_COUNT

  for (let year = startYear; year < endYear; year++) {
    yearsList.push(year)
  }

  const handleNextClick = () => {
    setStartYear(startYear + DISPLAY_YEAR_COUNT)
  }

  const handlePrevClick = () => {
    setStartYear(startYear - DISPLAY_YEAR_COUNT)
  }

  const handleClick = (year: number) => {
    setSelected(year)
    onSelect?.(year)
  }

  const handleReset = () => {
    setSelected(null)
    onReset?.()
  }

  const isOutSide = (year: number) => {
    return year > currentYear
  }

  const disableNext = startYear + DISPLAY_YEAR_COUNT > currentYear

  return (
    <ScrollWrapper>
      <div className={cn("year-picker w-max min-w-76 px-3", className)}>
        {children}
        <div className="year-nav flex justify-end">
          <button
            type="button"
            onClick={handlePrevClick}
            aria-label="Go to previous month"
            className="rdp-nav_button rdp-nav_year shrink-0">
            <CaretUp className="text-on-surface-subdued h-6 w-6 font-semibold" />
          </button>
          <button
            type="button"
            onClick={handleNextClick}
            aria-label="Go to next month"
            className={cn(
              "rdp-nav_button rdp-nav_year shrink-0",
              disableNext && "pointer-events-none cursor-not-allowed opacity-60",
            )}>
            <CaretDown className="text-on-surface-subdued h-6 w-6 font-semibold" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-x-4 gap-y-2" {...props}>
          {yearsList.map((year) => (
            <button
              key={year}
              name="year"
              type="button"
              className={cn(
                "rdp_year",
                selected === year && "rdp_year-selected",
                isOutSide(year) && "rdp_year-outside",
              )}
              onClick={() => handleClick(year)}>
              {year}
            </button>
          ))}
        </div>
        <CalendarFooter disabled={!selected} onClick={handleReset} />
      </div>
    </ScrollWrapper>
  )
}
